import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
  CollectionUtil,
  ElderQuantityService,
  Interval,
  Quantity,
  RestClientList,
} from '@elderbyte/ngx-starter'
import { LocalDate, Period, ZoneId } from '@js-joda/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { sum } from '../../../../functional.util'
import { mapInvolvedProcessPlanIds } from '../reports.util'
import { WorkCenterUtilizationReport } from './work-center-utilization-report'

@Injectable({
  providedIn: 'root',
})
export class UtilizationReportService extends RestClientList<any, string> {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private quantityService = inject(ElderQuantityService)

  // todo: move properties to separate service
  private readonly _completionInterval$ = new BehaviorSubject<Interval>(
    Interval.ofDay(LocalDate.now(), ZoneId.systemDefault()).moveStart(Period.ofDays(-7))
  )

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const httpClient = inject(HttpClient)
    super(environment.apiPath + '/reports/productivity/work-centers', httpClient)
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get completionInterval(): Interval {
    return this._completionInterval$.getValue()
  }

  public get completionInterval$(): Observable<Interval> {
    return this._completionInterval$.asObservable()
  }

  public set completionInterval(interval: Interval) {
    if (this.completionInterval !== interval && interval?.start?.isBefore(interval?.end)) {
      this._completionInterval$.next(interval)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public groupByField(
    records: WorkCenterUtilizationReport[],
    field: string
  ): WorkCenterUtilizationReport[] {
    return Array.from(
      CollectionUtil.groupByField<string, WorkCenterUtilizationReport>(records, field)
    ).map(([fieldValue, records]) => ({
      totalWeight: this.sumWeights(records),
      processPlanIds: mapInvolvedProcessPlanIds(records),
      totalProducedProduct: records.map((r) => r.totalProducedProduct).reduce(sum, 0),
      totalItems: records.map((r) => r.totalItems).reduce(sum, 0),
      workCenterName: records[records.length - 1].workCenterName,
      workCenterNumber: records[records.length - 1].workCenterNumber,
      manufactureFacility: records[records.length - 1].manufactureFacility,
      date: records[records.length - 1].date,
      totalDuration: records[records.length - 1].totalDuration,
      workCenterId: records[records.length - 1].workCenterId,
      [field]: fieldValue, // overwrite dynamically
    }))
  }

  public sumWeights(workCenterRecords: WorkCenterUtilizationReport[]): Quantity {
    const quantities: Quantity[] = workCenterRecords.map((r) => r.totalWeight)
    return this.quantityService.sum(quantities)
  }
}
